import type { Logger } from "@1js/office-online-telemetry";
import { LogCategory, TraceLevel } from "@1js/office-online-telemetry";

export class PPTLiveFrameManager {
  private logger: Logger;
  private instanceId: string;
  private messageListener: (domEvent: MessageEvent) => void;
  private messageListeners: ((data: any) => void)[];

  private iframe: HTMLIFrameElement | undefined = undefined;
  private iframeDomain: string | undefined = undefined;

  public constructor(logger: Logger, instanceId: string) {
    this.logger = logger;
    this.instanceId = instanceId;
    this.messageListeners = [];
    this.messageListener = (domEvent: MessageEvent) => {
      this.onMessage(domEvent);
    };
  }

  public dispose() {
    this.logger.sendTraceTag(
      0x1e3d2251 /* tag_4psjr */,
      LogCategory.msoulscat_Wac_PptLive,
      TraceLevel.Info,
      `PPTLiveFrameManager ${this.instanceId} disposing. Removing iframe: ${
        this.iframe !== undefined
      }`
    );

    if (this.iframe !== undefined) {
      this.iframe.parentElement?.removeChild(this.iframe);
    }
    this.iframe = undefined;
    this.iframeDomain = undefined;

    window.removeEventListener("message", this.messageListener);
  }

  public addMessageListener(listener: (data: any) => void): void {
    this.messageListeners.push(listener);
  }

  public tryLoadIFrame(
    container: HTMLElement,
    domain: string,
    endpoint: string,
    inputs: { name: string; value: string }[]
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (this.iframe !== undefined) {
        reject(
          "PPTLiveFrameManager tryCreateForm called after iframe already created"
        );
        return;
      }

      window.addEventListener("message", this.messageListener);
      this.iframeDomain = `https://${domain}`;
      const url = `https://${domain}/${endpoint}`;

      this.iframe = this.createIframe();
      container.append(this.iframe);

      const win = this.iframe.contentWindow;
      if (!win) {
        this.logger.sendTraceTag(
          0x1e3d2250 /* tag_4psjq */,
          LogCategory.msoulscat_Wac_PptLive,
          TraceLevel.Info,
          `PPTLiveFrameManager ${this.instanceId} iframe content window is empty`
        );
        reject("iframe content window is empty");
        return;
      }

      const form = this.createForm(win, url, inputs);

      this.iframe.onload = () => {
        this.logger.sendTraceTag(
          0x1e3d224f /* tag_4psjp */,
          LogCategory.msoulscat_Wac_PptLive,
          TraceLevel.Info,
          `PPTLiveHandler ${this.instanceId} iframe loaded`
        );
        resolve();
      };

      form.onerror = (ev: string | Event) => {
        this.logger.sendTraceTag(
          0x1e3d224e /* tag_4psjo */,
          LogCategory.msoulscat_Wac_PptLive,
          TraceLevel.Info,
          `PPTLiveHandler ${this.instanceId} iframe failed to load. Error: ${ev}`
        );
        reject("form error");
      };

      form.submit();
    });
  }

  private createIframe(): HTMLIFrameElement {
    const iframe = window.document.createElement("iframe");
    iframe.height = "100%";
    iframe.width = "100%";
    iframe.referrerPolicy = "origin";

    return iframe;
  }

  private createForm(
    win: Window,
    url: string,
    inputs: { name: string; value: string }[]
  ): HTMLFormElement {
    const form = win.document.createElement("form");
    form.setAttribute("action", url);
    form.setAttribute("method", "POST");
    win.document.body.append(form);

    inputs.forEach((input: { name: string; value: string }) => {
      const inputEl = win!.document.createElement("input");
      inputEl.setAttribute("type", "hidden");
      inputEl.setAttribute("name", input.name);
      inputEl.setAttribute("value", input.value);
      form.appendChild(inputEl);
    });

    return form;
  }

  private onMessage(domEvent: MessageEvent) {
    if (
      this.iframeDomain === undefined ||
      domEvent.origin !== this.iframeDomain
    ) {
      // Ignore messages that were not sent from our iframe
      return;
    }

    const data: any = domEvent.data;

    if (!data) {
      this.logger.sendTraceTag(
        0x1e3d224d /* tag_4psjn */,
        LogCategory.msoulscat_Wac_PptLive,
        TraceLevel.Error,
        `PPTLiveFrameManager ${this.instanceId} ignoring malformed post message from iframe`
      );
      return;
    }

    if (data.postMessageId !== this.instanceId) {
      this.logger.sendTraceTag(
        0x1e3cf019 /* tag_4ppaz */,
        LogCategory.msoulscat_Wac_PptLive,
        TraceLevel.Error,
        `PPTLiveFrameManager ${this.instanceId} ignoring message for instanceId ${data.postMessageId}`
      );
      return;
    }

    this.messageListeners.forEach((listener) => {
      listener(data);
    });
  }
}
